'use client';

import posthog from 'posthog-js';
import { RefreshCw } from 'lucide-react';
import { tw } from '@tw';
import { BAR_H } from '@/components/ui/bar';
import { useAppVersionStore } from '@/lib/stores/app-version.store';
import { BUILD_SHA } from '@/lib/version';

const LABEL = 'Update available';
const TITLE = 'Reload to get the latest version of Meetmy';

/**
 * Appears only once `VersionWatcher` has seen a newer deployment go live.
 *
 * The fallback path, not the main one: anyone who clicks a nav link gets the new build
 * automatically, so the only people who see this are parked on one screen for hours.
 *
 * One shape, one home — an icon circle in the site bar, beside the burger, and
 * it wears `BAR_H` like everything else free-standing there: it sat 4px shorter
 * than its neighbours until 2026-08-21, which is the drift that constant exists
 * to make impossible.
 * Deliberately not inside the menu: someone parked on one screen is exactly
 * the person who never opens one, so their only manual route must not live
 * behind a tap. (The sidebar-row and mobile-header shapes died with those two
 * surfaces, 2026-08-21.)
 */
export function UpdateAvailableButton({ className }: { className?: string }) {
  const updateAvailable = useAppVersionStore((s) => s.updateAvailable);

  if (!updateAvailable) return null;

  const reload = () => {
    posthog.capture('app_version_reloaded', {
      from: BUILD_SHA,
      trigger: 'nav_button',
    });
    window.location.reload();
  };

  return (
    <button
      type="button"
      onClick={reload}
      aria-label={`${LABEL}. ${TITLE}`}
      title={TITLE}
      className={tw(
        BAR_H,
        'flex w-13 items-center justify-center rounded-full bg-brand text-on-brand',
        'transition duration-150 active:scale-90 motion-reduce:active:scale-100',
        className
      )}
    >
      <RefreshCw className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
    </button>
  );
}
